import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

export const WEEKDAY_OPTIONS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const WEEKDAY_SHORT_BY_NAME = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
};

const WEEKDAY_NAME_BY_INDEX = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

export const DEFAULT_WEEKLY_TIMEZONE = 'Europe/Oslo';
export const DEFAULT_WEEKLY_START_DAY = 'monday';
export const DEFAULT_MINIMUM_BOOKING_WEEKS = 1;
export const DEFAULT_WEEKLY_START_TIME = '14:00';
export const DEFAULT_WEEKLY_END_TIME = '15:00';

const isValidDay = day => WEEKDAY_OPTIONS.includes(day);
const TIME_VALUE_PATTERN = /^\d{2}:\d{2}$/;

export const isValidTimeValue = value => {
  if (typeof value !== 'string' || !TIME_VALUE_PATTERN.test(value)) {
    return false;
  }
  const [hours, minutes] = value.split(':').map(part => Number.parseInt(part, 10));
  return hours >= 0 && hours <= 23 && minutes === 0;
};

export const normalizeTimeValue = (value, fallback) => (isValidTimeValue(value) ? value : fallback);

const timeValueInMinutes = value => {
  const [hours, minutes] = value.split(':').map(part => Number.parseInt(part, 10));
  return hours * 60 + minutes;
};

const timeValueForHour = hour => `${hour}`.padStart(2, '0') + ':00';

export const isWeeklyStartTimeValue = value =>
  isValidTimeValue(value) && timeValueInMinutes(value) < timeValueInMinutes('23:00');

export const isWeeklyEndTimeAfterStartTime = (startTime, endTime) =>
  isValidTimeValue(startTime) &&
  isValidTimeValue(endTime) &&
  timeValueInMinutes(endTime) > timeValueInMinutes(startTime);

export const getNextWeeklyEndTime = startTime => {
  if (!isWeeklyStartTimeValue(startTime)) {
    return DEFAULT_WEEKLY_END_TIME;
  }

  return timeValueForHour(timeValueInMinutes(startTime) / 60 + 1);
};

export const normalizeWeekday = day => {
  const normalized = typeof day === 'string' ? day.toLowerCase() : null;
  return isValidDay(normalized) ? normalized : DEFAULT_WEEKLY_START_DAY;
};

export const getWeeklySlotEndDay = startDay => {
  const normalizedStart = normalizeWeekday(startDay);
  const startIndex = WEEKDAY_OPTIONS.indexOf(normalizedStart);
  const endIndex = startIndex === 0 ? WEEKDAY_OPTIONS.length - 1 : startIndex - 1;
  return WEEKDAY_OPTIONS[endIndex];
};

export const getWeeklySlotOptions = weekStartsOn => {
  const primaryStartDay = normalizeWeekday(weekStartsOn);
  const secondaryStartDay = primaryStartDay === 'friday' ? 'monday' : 'friday';

  return [primaryStartDay, secondaryStartDay].map(startDay => ({
    startDay,
    endDay: getWeeklySlotEndDay(startDay),
  }));
};

const normalizeMinimumBookingWeeks = value => {
  const parsedValue = Number.parseInt(value, 10);
  return parsedValue >= 1 && parsedValue <= 4 ? parsedValue : DEFAULT_MINIMUM_BOOKING_WEEKS;
};

const getLegacyWeeklySlots = weeklyAvailability => {
  if (Array.isArray(weeklyAvailability?.availableWeeklySlots)) {
    return weeklyAvailability.availableWeeklySlots;
  } else if (Array.isArray(weeklyAvailability?.weeklySlots)) {
    return weeklyAvailability.weeklySlots;
  }
  return [];
};

const deriveWeekStartDay = weeklyAvailability => {
  const weekStartDay =
    typeof weeklyAvailability?.weekStartDay === 'string'
      ? weeklyAvailability.weekStartDay.toLowerCase()
      : null;
  const weekStartsOn =
    typeof weeklyAvailability?.weekStartsOn === 'string'
      ? weeklyAvailability.weekStartsOn.toLowerCase()
      : null;

  if (isValidDay(weekStartDay)) {
    return weekStartDay;
  } else if (isValidDay(weekStartsOn)) {
    return weekStartsOn;
  }

  const enabledLegacySlot = getLegacyWeeklySlots(weeklyAvailability).find(
    slot => slot?.enabled === true
  );
  return normalizeWeekday(enabledLegacySlot?.startDay);
};

const normalizeBlockedDates = blockedDates => {
  return Array.isArray(blockedDates)
    ? blockedDates
        .map(dateRange => {
          const startDate = dateRange?.startDate || '';
          const endDate = dateRange?.endDate || '';
          return startDate && endDate ? { startDate, endDate } : null;
        })
        .filter(Boolean)
    : [];
};

export const normalizeWeeklyAvailability = (weeklyAvailability, fallbackTimeZone) => {
  const timeZone = weeklyAvailability?.timezone || fallbackTimeZone || DEFAULT_WEEKLY_TIMEZONE;
  const weekStartDay = deriveWeekStartDay(weeklyAvailability);
  const weekEndDay = getWeeklySlotEndDay(weekStartDay);
  const minimumWeeklyBooking = normalizeMinimumBookingWeeks(
    weeklyAvailability?.minimumWeeklyBooking ?? weeklyAvailability?.minimumBookingWeeks
  );
  const rawStartTime = normalizeTimeValue(weeklyAvailability?.startTime, DEFAULT_WEEKLY_START_TIME);
  const startTime = isWeeklyStartTimeValue(rawStartTime) ? rawStartTime : DEFAULT_WEEKLY_START_TIME;
  const fallbackEndTime = getNextWeeklyEndTime(startTime);
  const rawEndTime = normalizeTimeValue(weeklyAvailability?.endTime, fallbackEndTime);
  const endTime = isWeeklyEndTimeAfterStartTime(startTime, rawEndTime)
    ? rawEndTime
    : fallbackEndTime;

  return {
    timezone: timeZone,
    weekStartDay,
    weekStartsOn: weekStartDay,
    weekEndDay,
    startTime,
    endTime,
    minimumWeeklyBooking,
    minimumBookingWeeks: minimumWeeklyBooking,
    availableWeeklySlots: [{ startDay: weekStartDay, endDay: weekEndDay, enabled: true }],
    blockedDates: normalizeBlockedDates(weeklyAvailability?.blockedDates),
  };
};

export const createWeeklyAvailabilityPlan = (timezone, weeklyAvailability) => {
  const normalizedAvailability = Array.isArray(weeklyAvailability)
    ? normalizeWeeklyAvailability(null, timezone)
    : normalizeWeeklyAvailability(weeklyAvailability, timezone);
  const hasWeeklyAvailability = Array.isArray(weeklyAvailability)
    ? weeklyAvailability.some(slot => slot?.enabled)
    : !!normalizedAvailability.weekStartDay;
  const { weekStartDay, weekEndDay, startTime, endTime } = normalizedAvailability;

  return {
    type: 'availability-plan/time',
    timezone,
    entries: hasWeeklyAvailability
      ? WEEKDAY_OPTIONS.map(day => ({
          dayOfWeek: WEEKDAY_SHORT_BY_NAME[day],
          seats: 1,
          startTime: day === weekStartDay ? startTime : '00:00',
          endTime: day === weekEndDay ? endTime : '00:00',
        }))
      : [],
  };
};

const bookingDayName = (date, timeZone) => {
  const dayIndex = moment(date)
    .tz(timeZone)
    .startOf('day')
    .day();
  return WEEKDAY_NAME_BY_INDEX[dayIndex];
};

const bookingDurationInDays = (startDate, endDate, timeZone) => {
  const start = moment(startDate)
    .tz(timeZone)
    .startOf('day');
  const end = moment(endDate)
    .tz(timeZone)
    .startOf('day');
  return end.diff(start, 'days');
};

export const getWeeklyEndDate = (startDate, minimumWeeklyBooking, timeZone) => {
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
    return null;
  }

  const bookingTimeZone = timeZone || DEFAULT_WEEKLY_TIMEZONE;
  const bookingWeeks = normalizeMinimumBookingWeeks(minimumWeeklyBooking);

  return moment(startDate)
    .tz(bookingTimeZone)
    .startOf('day')
    .add(bookingWeeks * 7, 'days')
    .toDate();
};

const bookingTimeValue = (date, timeZone) =>
  moment(date)
    .tz(timeZone)
    .format('HH:mm');

const isDateOnlyBookingRange = (startDate, endDate, timeZone) => {
  return (
    bookingTimeValue(startDate, timeZone) === '00:00' &&
    bookingTimeValue(endDate, timeZone) === '00:00'
  );
};

const bookingDurationInInclusiveDays = (startDate, endDate, timeZone) => {
  const start = moment(startDate)
    .tz(timeZone)
    .startOf('day');
  const end = moment(endDate)
    .tz(timeZone)
    .startOf('day');
  return end.diff(start, 'days') + 1;
};

const blockedDateRangeOverlapsBooking = (blockedDateRange, startDate, endDate, timeZone) => {
  const { startDate: blockedStartDate, endDate: blockedEndDate } = blockedDateRange || {};

  if (!blockedStartDate || !blockedEndDate) {
    return false;
  }

  const bookingStart = moment(startDate)
    .tz(timeZone)
    .startOf('day');
  const bookingEnd = moment(endDate)
    .tz(timeZone)
    .startOf('day');
  const blockedStart = moment.tz(blockedStartDate, 'YYYY-MM-DD', timeZone).startOf('day');
  const blockedEndExclusive = moment
    .tz(blockedEndDate, 'YYYY-MM-DD', timeZone)
    .startOf('day')
    .add(1, 'day');

  return bookingStart.isBefore(blockedEndExclusive) && bookingEnd.isAfter(blockedStart);
};

export const getEnabledWeeklyStartDays = weeklyAvailability => [
  normalizeWeeklyAvailability(weeklyAvailability).weekStartDay,
];

export const isValidWeeklyStartDate = (date, weekStartDay, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }

  const bookingTimeZone = timeZone || DEFAULT_WEEKLY_TIMEZONE;
  return bookingDayName(date, bookingTimeZone) === normalizeWeekday(weekStartDay);
};

export const isValidWeeklyEndDate = (startDate, endDate, minimumWeeklyBooking, timeZone) => {
  const hasDates = startDate instanceof Date && endDate instanceof Date;

  if (!hasDates || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return false;
  }

  const bookingTimeZone = timeZone || DEFAULT_WEEKLY_TIMEZONE;
  const start = moment(startDate).tz(bookingTimeZone);
  const end = moment(endDate).tz(bookingTimeZone);
  const durationInDays = bookingDurationInDays(startDate, endDate, bookingTimeZone);
  const minimumDays = normalizeMinimumBookingWeeks(minimumWeeklyBooking) * 7;

  return end.isAfter(start) && durationInDays >= minimumDays && durationInDays % 7 === 0;
};

const dateStringInTimeZone = (date, timeZone) =>
  moment(date)
    .tz(timeZone)
    .format('YYYY-MM-DD');

const dateTimeInTimeZone = (dateString, timeValue, timeZone) =>
  moment.tz(`${dateString} ${timeValue}`, 'YYYY-MM-DD HH:mm', timeZone).toDate();

export const resolveWeeklyBookingDates = (bookingDates, weeklyAvailability, timeZone) => {
  const { startDate, endDate } = bookingDates || {};
  const hasDates = startDate instanceof Date && endDate instanceof Date;

  if (!hasDates || !weeklyAvailability) {
    return bookingDates;
  }

  const normalizedAvailability = normalizeWeeklyAvailability(weeklyAvailability, timeZone);
  const bookingTimeZone = normalizedAvailability.timezone || timeZone || DEFAULT_WEEKLY_TIMEZONE;
  const bookingStartDateString = dateStringInTimeZone(startDate, bookingTimeZone);
  const bookingEndDateString = moment(endDate)
    .tz(bookingTimeZone)
    .startOf('day')
    .subtract(1, 'day')
    .format('YYYY-MM-DD');

  return {
    startDate: dateTimeInTimeZone(
      bookingStartDateString,
      normalizedAvailability.startTime,
      bookingTimeZone
    ),
    endDate: dateTimeInTimeZone(
      bookingEndDateString,
      normalizedAvailability.endTime,
      bookingTimeZone
    ),
  };
};

export const validateWeeklyBookingDates = (bookingDates, weeklyAvailability, timeZone) => {
  const { startDate, endDate } = bookingDates || {};
  const hasDates = startDate instanceof Date && endDate instanceof Date;

  if (!weeklyAvailability || !hasDates) {
    return null;
  }

  const normalizedAvailability = normalizeWeeklyAvailability(weeklyAvailability, timeZone);
  const bookingTimeZone = normalizedAvailability.timezone || timeZone || DEFAULT_WEEKLY_TIMEZONE;
  const enabledStartDays = getEnabledWeeklyStartDays(normalizedAvailability);
  const startsBeforeEnd = moment(endDate)
    .tz(bookingTimeZone)
    .isAfter(moment(startDate).tz(bookingTimeZone));

  if (enabledStartDays.length === 0) {
    return { code: 'unavailable' };
  }

  if (!startsBeforeEnd) {
    return { code: 'endAfterStart' };
  }

  const selectedStartDay = bookingDayName(startDate, bookingTimeZone);
  const startsOnEnabledDay = enabledStartDays.includes(selectedStartDay);
  const dateOnlyBookingRange = isDateOnlyBookingRange(startDate, endDate, bookingTimeZone);

  if (!startsOnEnabledDay) {
    return { code: 'startDay', allowedStartDays: enabledStartDays };
  }

  if (!dateOnlyBookingRange) {
    const selectedEndDay = bookingDayName(endDate, bookingTimeZone);
    const expectedEndDay = getWeeklySlotEndDay(selectedStartDay);

    if (bookingTimeValue(startDate, bookingTimeZone) !== normalizedAvailability.startTime) {
      return { code: 'startTime', startTime: normalizedAvailability.startTime };
    } else if (selectedEndDay !== expectedEndDay) {
      return { code: 'endDay', expectedEndDay };
    } else if (bookingTimeValue(endDate, bookingTimeZone) !== normalizedAvailability.endTime) {
      return { code: 'endTime', endTime: normalizedAvailability.endTime };
    }
  }

  const durationInDays = dateOnlyBookingRange
    ? bookingDurationInDays(startDate, endDate, bookingTimeZone)
    : bookingDurationInInclusiveDays(startDate, endDate, bookingTimeZone);
  const minimumDays = normalizedAvailability.minimumBookingWeeks * 7;

  if (durationInDays < minimumDays) {
    return {
      code: 'minimum',
      minimumBookingWeeks: normalizedAvailability.minimumBookingWeeks,
    };
  }

  if (durationInDays % 7 !== 0) {
    return { code: 'wholeWeeks' };
  }

  const overlapsBlockedDate = normalizedAvailability.blockedDates.some(blockedDateRange =>
    blockedDateRangeOverlapsBooking(blockedDateRange, startDate, endDate, bookingTimeZone)
  );

  return overlapsBlockedDate ? { code: 'blocked' } : null;
};

export const isWeeklyBookingEndpointAvailable = (
  day,
  startDate,
  endDate,
  weeklyAvailability,
  timeZone
) => {
  const normalizedAvailability = normalizeWeeklyAvailability(weeklyAvailability, timeZone);
  const bookingTimeZone = normalizedAvailability.timezone || timeZone || DEFAULT_WEEKLY_TIMEZONE;
  const enabledStartDays = getEnabledWeeklyStartDays(normalizedAvailability);
  const dayStart = moment(day)
    .tz(bookingTimeZone)
    .startOf('day')
    .toDate();
  const dayEnd = moment(dayStart)
    .tz(bookingTimeZone)
    .startOf('day')
    .add(1, 'day')
    .toDate();
  const dayOverlapsBlockedDate = normalizedAvailability.blockedDates.some(blockedDateRange =>
    blockedDateRangeOverlapsBooking(blockedDateRange, dayStart, dayEnd, bookingTimeZone)
  );

  if (enabledStartDays.length === 0) {
    return false;
  }

  if (!startDate || endDate) {
    if (dayOverlapsBlockedDate) {
      return false;
    }

    return enabledStartDays.includes(bookingDayName(dayStart, bookingTimeZone));
  }

  return !validateWeeklyBookingDates(
    { startDate, endDate: dayStart },
    normalizedAvailability,
    bookingTimeZone
  );
};
